/**
 * <photo-upload> — made-to-order photo field for Forevervow PDP.
 *
 * Validates, transcodes (HEIC / oversized → JPEG) and previews the customer's
 * photo before Dawn's existing form submission carries it to /cart/add.
 *
 * Two rules this file exists to enforce:
 *   1. Never block the sale on a transcode failure — fall back to the original.
 *   2. Never let an oversized request leave the browser. Shopify's nginx returns
 *      HTML (not JSON) on 413, which makes Dawn's response.json() throw and
 *      leaves the add-to-cart button stuck in its loading state forever.
 */
if (!customElements.get('photo-upload')) {
  customElements.define(
    'photo-upload',
    class PhotoUpload extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-fv-photo]');
        this.dropzone = this.querySelector('[data-fv-dropzone]');
        this.preview = this.querySelector('[data-fv-preview]');
        this.thumb = this.querySelector('[data-fv-thumb]');
        this.filename = this.querySelector('[data-fv-filename]');
        this.filesize = this.querySelector('[data-fv-filesize]');
        this.resetBtn = this.querySelector('[data-fv-reset]');
        this.thumbBtn = this.querySelector('[data-fv-thumb-btn]');
        this.errorEl = this.querySelector('[data-fv-error]');
        this.warnEl = this.querySelector('[data-fv-warn]');

        if (!this.input) return;

        this.maxBytes = parseInt(this.dataset.maxBytes, 10) || 18874368;
        this.maxEdge = parseInt(this.dataset.maxEdge, 10) || 4000;
        this.minEdge = parseInt(this.dataset.minEdge, 10) || 800;

        this.copy = {
          type: this.dataset.errorType || "That doesn't look like an image.",
          size: this.dataset.errorSize || 'That photo is too large. Please choose one under 18MB.',
          missing: this.dataset.errorMissing || 'Please upload a photo of your pet so we can begin.',
          small: this.dataset.warnSmall || 'This photo is quite small — it may look soft at larger sizes.',
        };

        this.objectUrl = null;
        this.form = document.getElementById(this.dataset.formId) || this.closest('form');

        this.input.addEventListener('change', this.onChange.bind(this));
        this.resetBtn.addEventListener('click', this.reset.bind(this));
        // Clicking the photo reopens the picker — the shortcut people reach for
        // before they look for a link.
        if (this.thumbBtn) this.thumbBtn.addEventListener('click', () => this.input.click());

        this.dropzone.addEventListener('keydown', (e) => {
          // A <label> that has keyboard focus does NOT forward Enter/Space to its
          // input — that forwarding only happens for pointer clicks.
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.input.click();
          }
        });

        ['dragenter', 'dragover'].forEach((t) =>
          this.dropzone.addEventListener(t, (e) => {
            e.preventDefault();
            this.dropzone.classList.add('is-dragover');
          })
        );
        ['dragleave', 'drop'].forEach((t) =>
          this.dropzone.addEventListener(t, (e) => {
            e.preventDefault();
            this.dropzone.classList.remove('is-dragover');
          })
        );
        this.dropzone.addEventListener('drop', (e) => {
          const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (file) this.accept(file);
        });

        // MUST be on document (capture), not on the form.
        // submit's target IS the form, so listeners registered on the form —
        // capture flag or not — all run in the AT_TARGET phase, in registration
        // order. Which of <product-form> / <photo-upload> upgrades first is not
        // something we control. Only a real capture-phase ancestor is guaranteed
        // to run before Dawn's handler.
        this.onSubmitCapture = this.onSubmitCapture.bind(this);
        document.addEventListener('submit', this.onSubmitCapture, true);
      }

      disconnectedCallback() {
        this.releaseUrl();
        document.removeEventListener('submit', this.onSubmitCapture, true);
      }

      hasFile() {
        return !!(this.input && this.input.files && this.input.files.length > 0);
      }

      onSubmitCapture(evt) {
        if (!this.form || evt.target !== this.form) return; // not our form
        if (this.hasFile()) return; // all good — let Dawn take over

        evt.preventDefault();
        evt.stopImmediatePropagation();

        this.showError(this.copy.missing);
        this.classList.add('is-missing');
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.dropzone.focus({ preventScroll: true });
      }

      onChange() {
        const file = this.input.files && this.input.files[0];
        if (!file) return this.reset();
        this.accept(file);
      }

      async accept(file) {
        this.clearMessages();

        if (!file.type.startsWith('image/')) {
          this.setFiles(null);
          return this.showError(this.copy.type);
        }

        let out = file;
        let dims = null;
        try {
          const processed = await this.transcode(file);
          dims = processed.dims;
          // Re-encoding an already-small JPEG can make it bigger. Keep the smaller one.
          if (processed.file.size < file.size) out = processed.file;
        } catch (err) {
          // Browser can't decode this format (Chrome/Firefox + HEIC). Ship the
          // original — Shopify accepts it. Never block the sale on transcoding.
          out = file;
        }

        if (out.size > this.maxBytes) {
          this.setFiles(null);
          return this.showError(this.copy.size);
        }

        this.setFiles(out);
        if (dims && Math.min(dims.w, dims.h) < this.minEdge) this.showWarn(this.copy.small);
        this.showPreview(out);
      }

      transcode(file) {
        return new Promise((resolve, reject) => {
          const url = URL.createObjectURL(file);
          const img = new Image();
          img.onload = () => {
            URL.revokeObjectURL(url);
            const dims = { w: img.naturalWidth, h: img.naturalHeight };
            const scale = Math.min(1, this.maxEdge / Math.max(dims.w, dims.h));
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(dims.w * scale);
            canvas.height = Math.round(dims.h * scale);
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(
              (blob) => {
                if (!blob) return reject(new Error('toBlob returned null'));
                const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
                resolve({ file: new File([blob], name, { type: 'image/jpeg' }), dims });
              },
              'image/jpeg',
              0.92
            );
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('decode failed'));
          };
          img.src = url;
        });
      }

      setFiles(file) {
        const dt = new DataTransfer();
        if (file) dt.items.add(file);
        this.input.files = dt.files;
      }

      showPreview(file) {
        this.releaseUrl();
        this.objectUrl = URL.createObjectURL(file);
        this.thumb.src = this.objectUrl;
        this.filename.textContent = file.name;
        this.filesize.textContent = this.formatSize(file.size);
        this.preview.hidden = false;
        this.dropzone.hidden = true;
        this.classList.add('has-file');
        this.classList.remove('is-missing');
      }

      reset() {
        this.setFiles(null);
        this.releaseUrl();
        this.preview.hidden = true;
        this.dropzone.hidden = false;
        this.classList.remove('has-file');
        this.clearMessages();
        this.input.value = '';
      }

      /* Anything under 1MB — a WeChat-compressed photo, say — reads as
         "0.0 MB" in fixed MB, which looks like the upload failed. */
      formatSize(bytes) {
        if (bytes < 1048576) return Math.max(1, Math.round(bytes / 1024)) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
      }

      releaseUrl() {
        if (this.objectUrl) {
          URL.revokeObjectURL(this.objectUrl);
          this.objectUrl = null;
        }
      }

      showError(msg) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      showWarn(msg) {
        this.warnEl.textContent = msg;
        this.warnEl.hidden = false;
      }

      clearMessages() {
        this.errorEl.hidden = true;
        this.warnEl.hidden = true;
        this.errorEl.textContent = '';
        this.warnEl.textContent = '';
      }
    }
  );
}
